import { ActiveModuleMap, NAV_ROUTES, NavRoute, isNavRouteReachable } from '../navigation/navigation-routes';

export interface LandingContext {
  roles: readonly string[];
  activeModules: ActiveModuleMap | null | undefined;
  brandModules: readonly string[];
}

const ADMIN_TIER = ['ADMIN', 'MASTERADMIN'];

/**
 * docs/specs/2026-10-08-member-landing-page.md, "Landing table". The order IS the decision: the first
 * row whose role the member holds and whose route they can open wins. Operational roles precede the
 * admin tier, and ordering precedes the timesheets kiosk (a shop tablet opens ordering by default).
 */
const LANDING_TABLE: ReadonlyArray<{ roles: readonly string[]; paths: readonly string[] }> = [
  { roles: ['ACCOUNTANT'], paths: ['expenses'] },
  { roles: ['WAREHOUSEMANAGER', 'WAREHOUSESUPERVISOR', 'WAREHOUSECLERK'], paths: ['warehouse-orders'] },
  { roles: ['SALESREP'], paths: ['salesrep'] },
  { roles: ['PROCUREMENTMANAGER'], paths: ['procurement'] },
  { roles: ['TIMESHEETSADMIN', 'TIMESHEETSMANAGER', 'TIMESHEETSSTATION', 'EMPLOYEE'], paths: ['timesheets'] },
  { roles: ['MARKETING-MANAGER'], paths: ['marketing-campaigns'] },
  { roles: ADMIN_TIER, paths: ['order-inbox'] },
  { roles: ADMIN_TIER, paths: ['warehouse-orders', 'salesrep', 'procurement', 'expenses', 'timesheets'] },
  { roles: ADMIN_TIER, paths: ['organization-admin'] },
];

function reachable(ctx: LandingContext, routes: readonly NavRoute[]): NavRoute[] {
  return routes.filter((route) => isNavRouteReachable(route, ctx.roles, ctx.activeModules, ctx.brandModules));
}

/** Where the table alone sends this member: its first matching row, else the first route they can open. */
export function tableLandingRoute(ctx: LandingContext, routes: readonly NavRoute[] = NAV_ROUTES): NavRoute | null {
  const open = reachable(ctx, routes);
  for (const row of LANDING_TABLE) {
    if (!row.roles.some((role) => ctx.roles.includes(role))) continue;
    for (const path of row.paths) {
      const route = open.find((candidate) => candidate.path === path);
      if (route) return route;
    }
  }
  return open[0] ?? null;
}

/** The modules a member could be pinned to: one route per module key, in navigation order. */
export function landingModuleOptions(ctx: LandingContext, routes: readonly NavRoute[] = NAV_ROUTES): NavRoute[] {
  const seen = new Set<string>();
  return reachable(ctx, routes).filter((route) => {
    if (seen.has(route.moduleName)) return false;
    seen.add(route.moduleName);
    return true;
  });
}

/**
 * The path a member lands on, or null to stay on home. A pinned `landingModule` wins only while the
 * member can still open it; anything else (unknown, switched off, roles changed) falls to the table.
 */
export function landingPath(
  ctx: LandingContext & { landingModule?: string | null },
  routes: readonly NavRoute[] = NAV_ROUTES
): string | null {
  const pinned = ctx.landingModule
    ? landingModuleOptions(ctx, routes).find((route) => route.moduleName === ctx.landingModule)
    : undefined;
  const route = pinned ?? tableLandingRoute(ctx, routes);
  return route ? `/app/${route.path}` : null;
}
